import { Box, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { adminApiService } from "../../../utils/api";
import { GfShoppingListFlyerItem, MuiFilterRequestDto } from "@swagger/typescript-fetch-goflyer";
import { useTranslation } from "react-i18next";
import BarChartIcon from "@mui/icons-material/BarChart";

import { messages } from "../../../locales/messages";
import { toastError } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useShoppingListReportTableColumns } from "./useShoppingListReportTableColumns";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { TableWrapper, RightSearchSection } from "./styled";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { ShoppingListChartModal } from "./ShoppingListChartModal";
import { GridFilterModel } from "@mui/x-data-grid";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

const ShoppingListReportPage = () => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();

  const { paginationModel, sortModel, handleTableChange } = useQueryParams();

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [shoppingListReportData, setShoppingListReportData] =
    useState<TableData<GfShoppingListFlyerItem>>();
  const [chartModalOpen, setChartModalOpen] = useState(false);

  const columns = useShoppingListReportTableColumns();

  const fetchShoppingListReportListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfShoppingListFlyerItemAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setShoppingListReportData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
      (error) => {
        console.error("Error fetching shopping list reports:", error);
        toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchShoppingListReportList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.getManyBaseGfShoppingListFlyerItemAdminControllerGfShoppingListFlyerItem(
          undefined,
          undefined,
          undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        ),
      (response) => {
        setShoppingListReportData((prev) => ({
          ...prev,
          rows: response.data,
          total: response?.total,
        }));
      },
      (error) => {
        console.error("Error fetching shopping list reports:", error);
        toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
      },
    );
  };

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchShoppingListReportListWithFilter(filterModel);
    } else {
      fetchShoppingListReportList();
    }
  }, [paginationModel, sortModel, filterModel]);

  return (
    <SetupPageContainer data-testid="shoppinglistreportpage-test-id">
      <AccountCardWrapper title={"Shopping List Report"}>
        <Box data-testid="view-page">
          <RightSearchSection>
            <Button
              variant="contained"
              startIcon={<BarChartIcon />}
              onClick={() => setChartModalOpen(true)}
              sx={{
                backgroundColor: "#f58220",
                "&:hover": {
                  backgroundColor: "#d97320",
                },
              }}
            >
              View Charts
            </Button>
          </RightSearchSection>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              columns={columns}
              rows={shoppingListReportData?.rows}
              total={paginationLoading ? 0 : shoppingListReportData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>
        </Box>
      </AccountCardWrapper>
      <ShoppingListChartModal
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
      />
    </SetupPageContainer>
  );
};

export default ShoppingListReportPage;
